import React, { useState } from 'react';
import { Factory, Search } from 'lucide-react';
import type { ApplianceBrand } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '../../lib/supabase';
import { toast } from '@/components/ui/use-toast';

export const BRANDS: { name: ApplianceBrand; location: string; description?: string }[] = [
  { name: 'Perlick', location: 'Wisconsin', description: 'Luxury refrigeration specialist' },
  { name: 'U-Line', location: 'Wisconsin', description: 'Built-in modular refrigeration' },
  { name: 'DCS', location: 'USA', description: 'Professional-grade appliances (Fisher & Paykel)' },
  { name: 'Capital', location: 'California', description: 'Premium cooking equipment' },
  { name: 'Dacor', location: 'California', description: 'Luxury kitchen appliances (Samsung)' },
  { name: 'American Range', location: 'California', description: 'Professional cooking equipment' },
  { name: 'Thermador', location: 'California', description: 'Premium kitchen appliances (BSH)' },
  { name: 'Marvel', location: 'Michigan', description: 'Premium refrigeration' },
  { name: 'Gaggenau', location: 'Pennsylvania', description: 'Luxury appliances (BSH)' },
  { name: 'Brown', location: 'Tennessee', description: 'Traditional appliances' },
  { name: 'Summit', location: 'New York', description: 'Specialty appliances' },
  { name: 'Heartland', location: 'USA', description: 'Classic-style appliances' },
  { name: 'Crosley', location: 'Kentucky', description: 'Professional and elite appliances' },
  { name: 'Whirlpool', location: 'Michigan', description: 'Full range of appliances' },
  { name: 'Maytag', location: 'Michigan', description: 'Durable home appliances' },
  { name: 'KitchenAid', location: 'Michigan', description: 'Premium kitchen appliances' },
  { name: 'Amana', location: 'Michigan', description: 'Value-focused appliances' },
  { name: 'Jenn-Air', location: 'Michigan', description: 'Luxury kitchen appliances' },
  { name: 'GE', location: 'Kentucky', description: 'Full range of appliances' },
  { name: 'GE Profile', location: 'Kentucky', description: 'Premium appliance line' },
  { name: 'GE Café', location: 'Kentucky', description: 'Professional-style appliances' },
  { name: 'Monogram', location: 'Kentucky', description: 'Luxury appliance line' },
  { name: 'Hotpoint', location: 'Kentucky', description: 'Value-focused appliances' },
  { name: 'Thor Kitchen', location: 'California', description: 'Professional-style appliances' },
  { name: 'Sub-Zero', location: 'Wisconsin', description: 'Luxury refrigeration' },
  { name: 'Wolf', location: 'Wisconsin', description: 'Professional cooking equipment' },
  { name: 'Cove', location: 'Wisconsin', description: 'Premium dishwashers' },
  { name: 'Viking', location: 'Mississippi', description: 'Professional kitchen appliances' },
  { name: 'Speed Queen', location: 'Wisconsin', description: 'Commercial-grade laundry' },
  { name: 'True Residential', location: 'Missouri', description: 'Commercial-style refrigeration' },
  { name: 'BlueStar', location: 'Pennsylvania', description: 'Professional cooking equipment' },
  { name: 'Big Chill', location: 'Pennsylvania', description: 'Retro-style appliances' },
  { name: 'Zephyr', location: 'California', description: 'Premium ventilation' },
];

interface BrandsTabProps {
  selectedBrands: ApplianceBrand[];
  onBrandToggle: (brand: ApplianceBrand) => void;
}

const BrandsTab: React.FC<BrandsTabProps> = ({ selectedBrands, onBrandToggle }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [saving, setSaving] = useState(false);

  const handleBrandToggle = async (brand: ApplianceBrand) => {
    try {
      const newBrands = selectedBrands.includes(brand)
        ? selectedBrands.filter(b => b !== brand)
        : [...selectedBrands, brand];

      // Update database
      const { error } = await supabase
        .from('companies')
        .update({ brands: newBrands })
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (error) throw error;

      // Update local state
      onBrandToggle(brand);

      toast({
        title: "Brand updated",
        description: selectedBrands.includes(brand) ? "Brand removed" : "Brand added",
      });
    } catch (err) {
      console.error('Error updating brands:', err);
      toast({
        title: "Error",
        description: "Failed to update brands. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({ brands: selectedBrands })
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Brands updated successfully",
      });
    } catch (err) {
      console.error('Error saving brands:', err);
      toast({
        title: "Error",
        description: "Failed to save brands. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredBrands = BRANDS.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brand.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (brand.description && brand.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-medium">Appliance Brands</h2>
        <div className="flex items-center mt-1">
          <span className="text-muted-foreground">Select the brands your company services</span>
          {selectedBrands.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {selectedBrands.length} selected
            </Badge>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-muted-foreground" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBrands.map(({ name, location, description }) => (
          <Card
            key={name}
            onClick={() => handleBrandToggle(name)}
            className={cn(
              "cursor-pointer transition-all hover:shadow-md",
              selectedBrands.includes(name) && "border-primary bg-primary/5"
            )}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start space-x-4">
                <div className={cn(
                  "p-2 rounded-lg transition-colors",
                  selectedBrands.includes(name)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}>
                  <Factory className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <CardTitle className={cn(
                    "text-lg",
                    selectedBrands.includes(name) && "text-primary"
                  )}>
                    {name}
                  </CardTitle>
                  <div className={cn(
                    "text-sm",
                    selectedBrands.includes(name) ? "text-primary/70" : "text-muted-foreground"
                  )}>
                    <div className="font-medium">{location}</div>
                    {description && <div>{description}</div>}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </>
  );
};

export default BrandsTab;